import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { TrendingUp, Users, FileCheck, AlertTriangle, Star, X } from 'lucide-react';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardAnalytics({ isOpen, onClose }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchAnalytics();
    }
  }, [isOpen]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/analytics/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Chart configurations
  const statusChartData = analytics ? {
    labels: ['Draft', 'Pending', 'Approved', 'Rejected'],
    datasets: [{
      data: [
        analytics.status_distribution.draft,
        analytics.status_distribution.pending_review,
        analytics.status_distribution.approved,
        analytics.status_distribution.rejected
      ],
      backgroundColor: ['#64748B', '#F59E0B', '#10B981', '#EF4444'],
      borderWidth: 0
    }]
  } : null;

  const monthlyTrendData = analytics ? {
    labels: analytics.monthly_trends.labels.map(m => {
      const [year, month] = m.split('-');
      return new Date(year, month - 1).toLocaleString('default', { month: 'short' });
    }),
    datasets: [
      {
        label: 'Created',
        data: analytics.monthly_trends.created,
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Approved',
        data: analytics.monthly_trends.approved,
        borderColor: '#10B981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  } : null;

  const ratingDistributionData = analytics ? {
    labels: ['Poor (1-3)', 'Average (3.5-5)', 'Good (5.5-7.5)', 'Excellent (8-10)'],
    datasets: [{
      label: 'Inspections',
      data: [
        analytics.rating_distribution['1-3'],
        analytics.rating_distribution['3.5-5'],
        analytics.rating_distribution['5.5-7.5'],
        analytics.rating_distribution['8-10']
      ],
      backgroundColor: ['#EF4444', '#F59E0B', '#3B82F6', '#10B981']
    }]
  } : null;

  const categoryAveragesData = analytics ? {
    labels: Object.keys(analytics.category_averages).map(cat => 
      cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    ),
    datasets: [{
      label: 'Average Rating',
      data: Object.values(analytics.category_averages),
      backgroundColor: 'rgba(198, 163, 74, 0.8)',
      borderColor: '#C6A34A',
      borderWidth: 1
    }]
  } : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-6 h-6 text-cyan-500" />
            <h2 className="text-lg sm:text-xl font-heading font-bold text-slate-900">Analytics Dashboard</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : analytics ? (
          <div className="p-4 sm:p-6 space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg p-4 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <FileCheck className="w-5 h-5 opacity-80" />
                  <span className="text-sm opacity-80">Total Reports</span>
                </div>
                <p className="text-2xl sm:text-3xl font-bold">{analytics.total_inspections}</p>
              </div>
              
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg p-4 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-5 h-5 opacity-80" />
                  <span className="text-sm opacity-80">Approved</span>
                </div>
                <p className="text-2xl sm:text-3xl font-bold">{analytics.status_distribution.approved}</p>
              </div>
              
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg p-4 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 opacity-80" />
                  <span className="text-sm opacity-80">Pending</span>
                </div>
                <p className="text-2xl sm:text-3xl font-bold">{analytics.status_distribution.pending_review}</p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-4 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 opacity-80" />
                  <span className="text-sm opacity-80">This Month</span>
                </div>
                <p className="text-2xl sm:text-3xl font-bold">{analytics.current_month_stats.created}</p>
              </div>
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Monthly Trend */}
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Monthly Trend (Last 6 Months)</h3>
                <div className="h-64">
                  {monthlyTrendData && (
                    <Line
                      data={monthlyTrendData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { position: 'bottom' } },
                        scales: {
                          y: { beginAtZero: true, ticks: { stepSize: 1 } }
                        }
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Status Distribution */}
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Status Distribution</h3>
                <div className="h-64 flex items-center justify-center">
                  {statusChartData && (
                    <Doughnut
                      data={statusChartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { position: 'bottom' } }
                      }}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Rating Distribution */}
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Rating Distribution</h3>
                <div className="h-64">
                  {ratingDistributionData && (
                    <Bar
                      data={ratingDistributionData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false } },
                        scales: {
                          y: { beginAtZero: true, ticks: { stepSize: 1 } }
                        }
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Category Averages */}
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Average Rating by Category</h3>
                <div className="h-64">
                  {categoryAveragesData && Object.keys(analytics.category_averages).length > 0 && (
                    <Bar
                      data={categoryAveragesData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        indexAxis: 'y',
                        plugins: { legend: { display: false } },
                        scales: {
                          x: { beginAtZero: true, max: 10 }
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* QC Performance (only shown to master/reviewer) */}
            {analytics.qc_performance && analytics.qc_performance.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">QC Engineer Performance</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">QC Engineer</th>
                        <th className="text-center py-2 px-3 font-semibold text-slate-600">Total</th>
                        <th className="text-center py-2 px-3 font-semibold text-slate-600">Approved</th>
                        <th className="text-center py-2 px-3 font-semibold text-slate-600">Pending</th>
                        <th className="text-center py-2 px-3 font-semibold text-slate-600">Rejected</th>
                        <th className="text-center py-2 px-3 font-semibold text-slate-600">Approval Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.qc_performance.map((qc, index) => (
                        <tr key={index} className="border-b border-slate-100">
                          <td className="py-2 px-3 font-medium text-slate-900">{qc.name}</td>
                          <td className="text-center py-2 px-3">{qc.total}</td>
                          <td className="text-center py-2 px-3 text-emerald-600">{qc.approved}</td>
                          <td className="text-center py-2 px-3 text-amber-600">{qc.pending}</td>
                          <td className="text-center py-2 px-3 text-red-600">{qc.rejected}</td>
                          <td className="text-center py-2 px-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              qc.total > 0 
                                ? (qc.approved / qc.total) >= 0.8 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : (qc.approved / qc.total) >= 0.5 
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-red-100 text-red-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {qc.total > 0 ? Math.round((qc.approved / qc.total) * 100) : 0}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Top Defect Areas */}
            {analytics.top_defect_areas && analytics.top_defect_areas.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Top Defect Areas</h3>
                <div className="space-y-2">
                  {analytics.top_defect_areas.map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <span className="text-xs font-medium text-slate-500 w-6">{index + 1}.</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-700">{item.area}</span>
                          <span className="text-sm text-slate-500">{item.count} defects</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-red-400 rounded-full"
                            style={{ width: `${(item.count / analytics.top_defect_areas[0].count) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-20 text-slate-500">
            Failed to load analytics data
          </div>
        )}
      </div>
    </div>
  );
}
